/**
 * Roles and discrete permissions (ACC-01, ACC-02). Permissions for later phases are
 * defined so accounts can be provisioned once; nothing in Phase 1 grants or checks them.
 */
export const ROLES = ['patient', 'navigator', 'expert', 'guest'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<Role, string> = {
  patient: 'Patient',
  navigator: 'Care Navigator',
  expert: 'Physician',
  guest: 'Guest',
};

export const PERMISSIONS = {
  'cases.manage': { phase: 1, label: 'Full case management' },
  'cases.manage_limited': { phase: 1, label: 'Limited case management' },
  'accounts.invite': { phase: 1, label: 'Issue account invitations' },
  'accounts.manage': { phase: 1, label: 'Enable, disable and flag accounts' },
  'directory.view': { phase: 1, label: 'Search the directory' },
  'admin.audit': {
    phase: 1,
    label: 'Admin: restore removed records and guests, remove an authenticator app',
  },
  'admin.super': { phase: 1, label: 'Super admin: edit any account, its role and permissions' },
  'payables.manage': { phase: 3, label: 'Manage physician payables' },
  'config.manage': { phase: 5, label: 'Manage configuration' },
} as const;
export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

/** What a newly invited account of each role receives. */
export const DEFAULT_PERMISSIONS: Record<Role, readonly Permission[]> = {
  patient: [],
  navigator: ['cases.manage', 'accounts.invite', 'accounts.manage', 'directory.view'],
  expert: [],
  guest: [],
};

/** Staff sign in to the console; patients to the portal; physicians and guests do not sign in in Phase 1. */
export type Realm = 'staff' | 'patient';
export function realmOf(role: Role): Realm | null {
  if (role === 'navigator') return 'staff';
  if (role === 'patient') return 'patient';
  return null;
}

export function canManageCases(perms: readonly Permission[]): boolean {
  return perms.includes('cases.manage') || perms.includes('cases.manage_limited');
}

/** Actions reserved for full case management (CON-11, CON-12, overrides). */
export function canManageCasesFully(perms: readonly Permission[]): boolean {
  return perms.includes('cases.manage');
}
